import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { get, snapshotFamily, useDesk } from './api/desk';
import { cancelOrder, isOpen, useTrading, type Action, type Order, type Position } from './api/trading';
import { AccountBar, PhoneAccount } from './workspace/AccountBar';
import { Dock } from './workspace/Dock';
import { Toasts, useFillAlerts, useToasts } from './workspace/Toasts';
import type { Contract, DeskSnapshot, SavedView } from './api/generated/types.gen';
import { Depth } from './workspace/Depth';
import { buyBand } from './workspace/buyBand';
import { PhoneBar, PhoneNav, type PhoneTab } from './workspace/MobileDesk';
import { useOneClick } from './workspace/oneClick';
import { PriceChart, type CoinStop, type TradeLevel } from './workspace/PriceChart';
import { SourcesDrawer } from './workspace/SourcesDrawer';
import { isLive, StrikeChips, type StrikeMark } from './workspace/StrikeRow';
import { Ticket, type Side, type TicketPreset } from './workspace/Ticket';
import { TopBar } from './workspace/TopBar';
import { priceCents, signed, tenths, time, toTenths } from './format';
import { familyOf, pathOf, placeFromPath, placeOfTicker, samePlace, sideName, upperOf, type Place } from './market';
import { MarketBar } from './workspace/MarketBar';
import { WindowPanel } from './workspace/WindowPanel';

const DOCK_KEY = 'ct.dock.collapsed';
const readDock = () => { try { return localStorage.getItem(DOCK_KEY) === '1'; } catch { return false; } };

// Plans 061-063: 15 and 30 seconds are built from recorded Coinbase trades.
const INTERVALS = ['15s', '30s', '1m', '5m', '15m', '1h', '4h'];
// The chart timeframe is remembered per horizon in this browser (a viewer convenience; nothing depends on it).
const intervalKey = (h: string) => `ct.interval.${h}`;
const readInterval = (h: string) => {
  try { const v = localStorage.getItem(intervalKey(h)); if (v && INTERVALS.includes(v)) return v; } catch { /* storage unavailable */ }
  return h === '15m' ? '1m' : '5m';
};


export function App() {
  const [place, setPlace] = useState<Place>(() => placeFromPath(location.pathname, location.search));
  const [ticker, setTicker] = useState<string | null>(() => new URLSearchParams(location.search).get('market'));
  const [replay, setReplay] = useState<string | null>(null);
  const [views, setViews] = useState<SavedView[]>([]);
  const [interval, setIntervalValue] = useState(() => readInterval(place.horizon));
  const chooseInterval = (t: string) => { setIntervalValue(t); try { localStorage.setItem(intervalKey(place.horizon), t); } catch { /* storage unavailable */ } };
  // Plan 041: gold reversal candles, on unless switched off in this browser.
  const [turns, setTurns] = useState(() => { try { return localStorage.getItem('ct.reversals') !== '0'; } catch { return true; } });
  const chooseTurns = (on: boolean) => { setTurns(on); try { localStorage.setItem('ct.reversals', on ? '1' : '0'); } catch { /* per-viewer convenience only */ } };
  // Plan 057: the settlement index's line on the chart, on unless turned off in this browser.
  const [indexOn, setIndexOn] = useState(() => { try { return localStorage.getItem('ct.indexLine') !== '0'; } catch { return true; } });
  const chooseIndex = (on: boolean) => { setIndexOn(on); try { localStorage.setItem('ct.indexLine', on ? '1' : '0'); } catch { /* per-viewer convenience only */ } };
  const [side, setSide] = useState<Side>('yes');
  const [sheet, setSheet] = useState(false);
  const [sources, setSources] = useState(false);
  const [update, setUpdate] = useState(false);
  // Rollover (plan 007): the contract last seen open while selected, and the close it rolled away from.
  const watched = useRef<string | null>(null);
  const [rolling, setRolling] = useState<number | null>(null);
  const [tick, setTick] = useState(Date.now());
  const { data: loaded, connection, error } = useDesk(place, ticker, replay);
  // A snapshot for another coin or horizon is never shown while the new one loads.
  const desk = loaded && (loaded.coin ?? 'BTC') === place.coin && (loaded.horizon ?? 'hourly') === place.horizon && snapshotFamily(loaded) === familyOf(place) ? loaded : null;
  const window15 = place.horizon === '15m', range = place.family === 'range';
  const trading = useTrading();
  const { toasts, notify, dismiss } = useToasts();
  useFillAlerts(trading.snapshot, notify);
  const [action, setAction] = useState<Action>('buy');
  const [modify, setModify] = useState<Order | null>(null);
  const [preset, setPreset] = useState<TicketPreset | null>(null);
  // Plan 040: with One-click on, a phone tap buys at once (a second tap within 1.5 s is ignored); plan 053: so does a click
  // on the desktop ticket's Buy YES / Buy NO.
  const [oneClick] = useOneClick();
  const lastTap = useRef(0);
  const tapBuy = (s: Side) => {
    setSide(s); setAction('buy'); setModify(null);
    if (!oneClick) { setSheet(true); return; }
    if (Date.now() - lastTap.current < 1500) return;
    lastTap.current = Date.now();
    setPreset({ action: 'buy', side: s, send: true, ticker: view?.selected?.ticker ?? undefined, nonce: Date.now() });
  };
  const [dockCollapsed, setDockCollapsed] = useState(readDock);
  const [phoneTab, setPhoneTab] = useState<PhoneTab>('desk');
  const collapseDock = (c: boolean) => { setDockCollapsed(c); try { localStorage.setItem(DOCK_KEY, c ? '1' : '0'); } catch { /* per-viewer convenience only */ } };
  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 1000);
    const onUpdate = () => setUpdate(true);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSheet(false); };
    window.addEventListener('desk-update', onUpdate); window.addEventListener('keydown', onKey);
    return () => { clearInterval(id); window.removeEventListener('desk-update', onUpdate); window.removeEventListener('keydown', onKey); };
  }, []);
  // The server clock drives the countdown; the local clock only interpolates between snapshots.
  const [serverClock, setServerClock] = useState({ server: Date.now() / 1000, local: Date.now() });
  useEffect(() => { if (desk) setServerClock({ server: desk.server_time, local: Date.now() }); }, [desk?.server_time]);
  const now = replay && desk ? desk.as_of : serverClock.server + (tick - serverClock.local) / 1000;
  const remember = (t: string | null) => { const u = new URL(location.href); if (t) u.searchParams.set('market', t); else u.searchParams.delete('market'); history.replaceState(null, '', u); };
  // Coin and horizon live in the path, range buckets in ?family=range and the contract in ?market=, so links and reloads land in the same place.
  const go = useCallback((p: Place, t: string | null = null) => {
    if (samePlace(p, place) && t == null) return;
    const u = new URL(location.href); u.pathname = pathOf(p);
    if (p.family) u.searchParams.set('family', p.family); else u.searchParams.delete('family');
    if (t) u.searchParams.set('market', t); else u.searchParams.delete('market');
    history.pushState(null, '', u);
    setPlace(p); setTicker(t); setReplay(null); setModify(null); setAction('buy'); setRolling(null);
    if (p.horizon !== place.horizon) setIntervalValue(readInterval(p.horizon));
  }, [place]);
  useEffect(() => {
    const back = () => {
      const p = placeFromPath(location.pathname, location.search);
      setPlace(p); setTicker(new URLSearchParams(location.search).get('market')); setIntervalValue(readInterval(p.horizon));
    };
    window.addEventListener('popstate', back);
    return () => window.removeEventListener('popstate', back);
  }, []);
  // Choosing a dock row or position from another market opens that market.
  const openTicker = (t: string) => { const p = placeOfTicker(t); if (p && !samePlace(p, place)) { go(p, t); return true; } return false; };
  const positions = trading.snapshot?.positions.filter(p => p.status === 'open') ?? [];
  const openOrders = (trading.snapshot?.orders ?? []).filter(isOpen);
  const heldIn = useCallback((t: string) => positions.find(p => p.ticker === t), [positions]);
  // Choosing a strike you hold sets the ticket up to exit it; any other strike to enter.
  const select = useCallback((c: Contract) => {
    watched.current = null; setRolling(null);
    setTicker(c.ticker); remember(c.ticker); setModify(null);
    const held = heldIn(c.ticker);
    if (held) { setAction('sell'); setSide(held.side); setPreset({ action: 'sell', side: held.side, quantity: Number(held.quantity), nonce: Date.now() }); }
    else setAction('buy');
  }, [heldIn]);
  const openTicket = (p: Position, review: boolean) => {
    if (!openTicker(p.ticker)) { setTicker(p.ticker); remember(p.ticker); }
    setModify(null); setAction('sell'); setSide(p.side);
    setPreset({ action: 'sell', side: p.side, quantity: Number(p.quantity), review, nonce: Date.now() });
    setSheet(true); setPhoneTab('desk');
  };
  const startModify = (o: Order) => { if (!openTicker(o.ticker)) { setTicker(o.ticker); remember(o.ticker); } setModify(o); setSheet(true); setPhoneTab('desk'); };
  const cancel = async (o: Order) => {
    try { await cancelOrder(o.id); notify(`Order cancelled · ${o.label}`, 'info'); trading.refresh(); }
    catch (e) { notify(e instanceof Error ? e.message : 'Cancel failed', 'down'); }
  };
  const currentHour = () => { setTicker(null); setReplay(null); remember(null); setRolling(null); };
  const freeze = async () => { if (!desk) return; setReplay(desk.view_id); setViews(await get<SavedView[]>('/api/v1/views')); };
  const period = window15 ? 900 : 3600;
  // While rolling over, pin only the contract that closes at the next boundary. Some hours the 5 PM ET event is
  // already listed and briefly looks like "the next event"; pinning it would strand the desk there.
  useEffect(() => {
    const s = desk?.selected;
    if (ticker !== null || !s) return;
    const close = Date.parse(s.close_time) / 1000;
    if (rolling != null && (close <= now || close > rolling + period + 1)) return;
    setRolling(null); setTicker(s.ticker); remember(s.ticker);
  }, [desk?.view_id, desk?.selected?.ticker, rolling]);
  // When the contract you are watching closes, follow the next one instead of sitting on the expired market.
  // A closed contract opened on purpose (from the dock or a link) was never seen open here, so it stays put.
  const chosenClose = desk?.selected && desk.selected.ticker === ticker ? Date.parse(desk.selected.close_time) / 1000 : null;
  useEffect(() => {
    if (replay || chosenClose == null || ticker == null) return;
    if (chosenClose > now) { watched.current = ticker; return; }
    if (watched.current === ticker) { watched.current = null; setRolling(chosenClose); setTicker(null); remember(null); }
  }, [ticker, chosenClose, Math.floor(now), replay]);
  // Between a close and the next contract's listing, show "opening" rather than a later expiry (such as the 5 PM
  // ET event) that would look like the current market.
  const nextClose = desk?.selected ? Date.parse(desk.selected.close_time) / 1000 : null;
  const waitingNext = rolling != null && (nextClose == null || nextClose <= now || nextClose > rolling + period + 1);
  const view = desk && waitingNext ? { ...desk, contracts: [], selected: null, book: null } : desk;
  const contracts = view?.contracts ?? [];
  const strikes = useMemo(() => contracts.map(c => ({ ticker: c.ticker, strike: Number(c.strike), live: isLive(c), upper: upperOf(c) })), [view?.contracts]);
  const chosen = contracts.find(c => c.ticker === ticker) ?? view?.selected ?? null;
  // A range bucket is a band on the chart; a tail or strike is a line.
  const chosenUpper = chosen ? upperOf(chosen) : null;
  const band = chosen && chosenUpper != null ? { low: Number(chosen.strike), high: chosenUpper } : null;
  const hourlyFamilies = desk?.markets?.find(m => m.coin === place.coin)?.horizons.find(h => h.horizon === 'hourly')?.families ?? [];
  const onRange = !window15 && hourlyFamilies.includes('range') && hourlyFamilies.includes('above_below')
    ? (r: boolean) => go({ coin: place.coin, horizon: 'hourly', ...(r ? { family: 'range' as const } : {}) }) : undefined;
  const pending = !!desk && !!ticker && desk.selected?.ticker !== ticker;
  const connected = connection === 'connected';
  // A switch of coin, horizon or strike reconnects on purpose; only a load slower than 3 s is worth a warning (plan 029).
  const [slowLoad, setSlowLoad] = useState(false);
  useEffect(() => {
    setSlowLoad(false);
    if (connection !== 'connecting') return;
    const t = setTimeout(() => setSlowLoad(true), 3000);
    return () => clearTimeout(t);
  }, [connection]);
  const troubled = !connected && connection !== 'replay' && (connection !== 'connecting' || slowLoad);
  const event = view?.selected ?? contracts[0];
  const marks = useMemo(() => {
    const m = new Map<string, StrikeMark>();
    for (const o of openOrders) m.set(o.ticker, { kind: 'order', label: `${o.action.toUpperCase()} ${Number(o.quantity) - Number(o.filled_quantity)}@${tenths(toTenths(o.limit_price)).replace('¢', '')}` });
    for (const p of positions) m.set(p.ticker, { kind: p.side, label: `+${Number(p.quantity)} ${sideName(p.side, p.ticker)}` });
    return m;
  }, [trading.snapshot]);
  // Positions on the contracts shown: their level on the coin chart (a range bucket's two edges), labelled with size,
  // entry and open P&L; and, for the selected one, the contract-price pane (plan 014). Past trades stay off the chart.
  const levels = positions.flatMap((p): TradeLevel[] => {
    const c = contracts.find(x => x.ticker === p.ticker), upper = c ? upperOf(c) : null;
    if (!c || (Number(c.strike) === 0 && upper == null)) return [];
    const pnl = p.open_pnl == null ? '' : ` ${signed(p.open_pnl, 2).replace(/^([+−])/, '$1$')}`;
    const label = `${sideName(p.side, p.ticker)} ${Number(p.quantity)} @ ${priceCents(p.average_price)}${pnl}`;
    const low = Number(c.strike) === 0 ? upper! : Number(c.strike);
    return [{ key: `${p.ticker}${p.side}`, side: p.side, low, high: Number(c.strike) === 0 ? null : upper, label, selected: c.ticker === chosen?.ticker }];
  });
  // Plan 063: their coin stops, at the swing price, while the order service is following one.
  const coinStops = positions.flatMap((p): CoinStop[] => contracts.some(x => x.ticker === p.ticker) && p.exit?.structure && p.exit.structure_stop != null && p.exit.structure_direction
    ? [{ key: `${p.ticker}${p.side}`, price: Number(p.exit.structure_stop), direction: p.exit.structure_direction }] : []);
  const chosenHeld = chosen ? heldIn(chosen.ticker) : undefined;
  const heldContract = chosen && chosenHeld ? { ticker: chosen.ticker, side: chosenHeld.side, name: sideName(chosenHeld.side, chosen.ticker), entry: Number(chosenHeld.average_price),
    takeProfit: chosenHeld.exit?.take_profit != null ? Number(chosenHeld.exit.take_profit) : null,
    stopLoss: chosenHeld.exit?.stop_loss != null ? Number(chosenHeld.exit.stop_loss) : null,
    floor: chosenHeld.exit?.stop_floor != null ? Number(chosenHeld.exit.stop_floor) : null,
    lock: chosenHeld.exit?.lock_trigger != null && chosenHeld.exit.lock_stop != null && !chosenHeld.exit.locked_at
      ? { at: Number(chosenHeld.exit.lock_trigger), to: Number(chosenHeld.exit.lock_stop) } : null } : null;
  const depthSide = modify?.side ?? (action === 'sell' && chosen && heldIn(chosen.ticker) ? heldIn(chosen.ticker)!.side : side);
  const mine = useMemo(() => {
    const bids = new Map<number, number>(), asks = new Map<number, number>();
    for (const o of openOrders) if (o.ticker === chosen?.ticker && o.side === depthSide) {
      const map = o.action === 'buy' ? bids : asks, c = toTenths(o.limit_price)!;
      map.set(c, (map.get(c) ?? 0) + Number(o.quantity) - Number(o.filled_quantity));
    }
    return { bids, asks };
  }, [trading.snapshot, chosen?.ticker, depthSide]);
  // The period the selected contract settles on, shaded on the chart: the 15-minute window, or the final hour of an
  // hourly contract (strike ladder or range), so both horizons show where "now" sits inside the period.
  const chosenEnd = chosen ? Date.parse(chosen.close_time) / 1000 : null;
  const periodSpan = chosen && chosenEnd != null
    ? { start: window15 && chosen.open_time ? Date.parse(chosen.open_time) / 1000 : chosenEnd - 3600, end: chosenEnd } : null;
  const notCurrentHour = !!desk?.selected && !!contracts.length && contracts.every(c => c.event_ticker !== desk.selected!.event_ticker);
  return <div className={`desk${replay ? ' replay' : ''}${sheet ? ' sheet-open' : ''} phone-${phoneTab}`}>
    <TopBar desk={desk} connection={connection} replay={replay} views={views} now={now} onLive={() => setReplay(null)} onFreeze={freeze} onView={setReplay} onSources={() => setSources(true)}
      account={<AccountBar trading={trading} notify={notify}/>}/>
    <div className="banners">
      {desk && troubled && <div role="status" className="banner error">{error || 'Reconnecting to the desk…'} Displayed values are not current until they revalidate.</div>}
      {update && <div className="banner">A workspace update is ready. <button onClick={() => window.dispatchEvent(new Event('desk-apply-update'))}>Reload when ready</button></div>}
    </div>
    <MarketBar desk={desk ?? loaded} place={place} now={now} onPlace={p => go(p)}/>
    {!desk ? <main className="loading"><h2>Connecting to the desk</h2><p>Prices, candles and {window15 ? '15-minute windows' : range ? 'hourly range buckets' : 'hourly contracts'} for {place.coin} appear as their sources report in.</p>{error && <p className="error">{error}</p>}</main>
      : <main className="workspace">
        <div className="main-col">
        <section className="panel chart-panel">
          <div className="chart-area">
            <div className="chart-tools">
              <div className="seg" role="group" aria-label="Chart timeframe">{INTERVALS.map(t => <button key={t} aria-pressed={interval === t} onClick={() => chooseInterval(t)}>{t}</button>)}</div>
              <button className="chip" aria-pressed={turns} onClick={() => chooseTurns(!turns)} data-testid="reversals-chip"
                title="Gold: the trend's structure broke. In a downtrend (lower swing tops and bottoms), the first candle to close above the last swing top; in an uptrend, the first to close below the last swing bottom. The dotted line runs from that swing. It never moves later. A reading aid, not a forecast."><i className="gold-dot"/>Reversals</button>
              <button className="chip" aria-pressed={indexOn} onClick={() => chooseIndex(!indexOn)} data-testid="index-chip"
                title={`CF Benchmarks ${desk.benchmark?.index_id ?? 'index'}: the price these contracts settle on, recorded once a second`}><i className="dot-line"/>Index</button>
            </div>
            <PriceChart desk={desk} interval={interval} reversalMarks={turns} indexLine={indexOn} strikes={strikes} selectedStrike={chosen && chosen.strike !== '0' && !band ? Number(chosen.strike) : null} selectedBand={band} levels={levels} coinStops={coinStops} contract={heldContract}
              window={periodSpan}/>
            {window15 && <WindowPanel desk={view!} now={now} side={side} onPick={s => { setSide(s); setAction('buy'); setModify(null); setSheet(true); }}/>}
          </div>
          {!window15 && <StrikeChips contracts={contracts} selected={chosen?.ticker ?? null} onSelect={select} onRange={onRange} range={range} marks={marks}
            status={rolling != null ? <span data-testid="rolling">Opening the next hour…</span>
              : notCurrentHour ? <button onClick={currentHour}>Back to current hour</button> : event ? <span>Closes {time(event.close_time)}</span> : null}/>}
        </section>
        <Dock trading={trading} now={now} collapsed={dockCollapsed} onCollapse={collapseDock}
          onSelect={t => { const c = contracts.find(x => x.ticker === t); if (c) select(c); else if (!openTicker(t)) { watched.current = null; setTicker(t); remember(t); } }}
          onClose={p => openTicket(p, true)} onExit={p => openTicket(p, false)} onModify={startModify} onCancel={cancel} notify={notify}
          focus={phoneTab === 'desk' ? null : phoneTab}/>
        </div>
        <div className="side" aria-label="Ticket and depth">
          <div className="sheet-head"><span/><button className="tool" onClick={() => setSheet(false)}>Done</button></div>
          <Ticket desk={view!} connected={connected} side={side} onSide={setSide} pending={pending} action={action} onAction={setAction}
            trading={trading} modify={modify} onModifyDone={() => setModify(null)} preset={preset} notify={notify}
            onPresetTaken={n => setPreset(p => p?.nonce === n ? null : p)} onBuyNow={tapBuy}/>
          <Depth desk={view!} side={depthSide} mine={mine}/>
        </div>
      </main>}
    <footer className="status">
      <span><b>Market price is not a calibrated probability.</b></span>
      <span>Chart: Coinbase {place.coin}/USD. Outcome: {desk?.settlement_index ?? 'CF Benchmarks index'} 60s average.</span>
      {desk && <span className="right">{replay ? 'REPLAY' : 'LIVE'} · view {desk.view_id.slice(0, 8)} · rev {desk.revision}</span>}
    </footer>
    {/* On phones the buy bar and the section nav share one sticky footer, so they stack instead of overlapping.
        It comes after the status footer so it stays on the screen's bottom edge at the end of the page. */}
    <div className="phone-footer">
      <PhoneAccount trading={trading}/>
      {view && <PhoneBar contract={view.selected} onOpen={tapBuy} buys={oneClick} band={buyBand(trading.snapshot?.account.limits)}/>}
      {desk && <PhoneNav tab={phoneTab} onTab={setPhoneTab} positions={positions.length} orders={openOrders.length}/>}
    </div>
    <div className="scrim sheet" onClick={() => setSheet(false)}/>
    {sources && desk && <SourcesDrawer desk={desk} onClose={() => setSources(false)}/>}
    <Toasts toasts={toasts} dismiss={dismiss}/>
  </div>;
}
